import ArrowRight from "@/app/assets/icons/arrow-right";
import { UEListData } from "@/app/interface/homepage";
import { formatIndianCurrency } from "@/app/utils/helper";
import { ButtonSize, ButtonVariant } from "@/stories/button/button";
import { IconButton } from "@/stories/icon-button/icon-button";
import Image from "next/image";
import Link from "next/link";
import React from "react";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
import { usePathname } from "next/navigation";
import { ThirdPartyData } from "@/app/interface/docvault";
// import { initBranch, trackEvent } from "@/app/utils/branchio";
import TopPicks from "@/app/assets/icons/top-picks";
import { Badge, BadgeScheme, BadgeVariant } from "@/stories/badge/badge";
import { getStaggeredDataMinPrice, updateStaggerdPrice } from "@/app/utils/helper/helper";
import { useCleverTapEvents } from "@/app/hooks/use-clevertap";
import { getUnlistedShareProductPath, toUnlistedShareSlug } from "@/app/unlisted-shares/[...id]/slug";

interface UeHeroCardProps extends UEListData {
    isPostlogin?: boolean;
    event?: string;
    thirdPartyData?: ThirdPartyData | undefined;
    userId?: string;
    refEvent?: string;
    phone?: string;
    email?: string;
    // active?: string;
}

const UEHeroCard: React.FC<UeHeroCardProps> = ({
    isPostlogin,
    event,
    // thirdPartyData,
    userId,
    email,
    phone,
    refEvent,
    // active,
    ...data
}) => {
    const pathName = usePathname();
    const { cleverTapTrackEvent } = useCleverTapEvents();

    const staggeredPrice =
        data?.staggeredPrice && data.staggeredPrice?.length > 0 ? data.staggeredPrice : 0;
    let staggeredPriceMinValue = 0;

    if (staggeredPriceMinValue === 0 && staggeredPrice && data?.price?.price) {
        const price = { priceStepSize: data.priceStepSize, unitPrice: data.price.price };
        updateStaggerdPrice(staggeredPrice, price);
        staggeredPriceMinValue = getStaggeredDataMinPrice(staggeredPrice) ?? 0;
    }

    // Generate the URL for crawlable links (SEO)
    const companySlug = toUnlistedShareSlug(data?.company?.displayName ?? "");
    const href = getUnlistedShareProductPath(data?.product ?? "", data?.company?.displayName);

    const handleClick = (e: React.MouseEvent) => {
        const eventName = isPostlogin ? event : `US_Listing_Page_View_Details_${companySlug}`;
        setAnalyticsEvent(href, eventName ?? "");

        if (isPostlogin) {
            e.preventDefault(); // Prevent Link navigation for post-login redirect
            if (refEvent) {
                cleverTapTrackEvent(refEvent, { email: email || "", phone: phone || "", userId: userId || "" });
            }
            // initBranch()
            //     .then(() => {
            //         if (refEvent) {
            //             trackEvent(refEvent, email || "", phone || "", userId || "");
            //         }
            //         trackEvent(
            //             "KYC_Low_Intent_Stocks_selected",
            //             thirdPartyData?.email || "",
            //             thirdPartyData?.phone || "",
            //             userId || "",
            //         );
            //     })
            //     .catch((error) => console.error("Error initializing Branch:", error));
            // const redirectUrlPost = `unlisted-shares/${companyName}`;
            window.location.href = href;
        } else {
            if (pathName && pathName.toLowerCase().indexOf("/refer") !== -1) {
                setAnalyticsEvent(href, "US_Rewards_" + companySlug);
                // initBranch()
                //     .then(() => {
                //         trackEvent(
                //             "US_Rewards_" + companyName,
                //         // cleverTapTrackEvent("US_Rewards_" + companyName, {});
                //             thirdPartyData?.email || "",
                //             thirdPartyData?.phone || "",
                //             userId || "",
                //         );
                //     })
                //     .catch((error) => console.error("Error initializing Branch:", error));
            }
            // Let the Link component handle the navigation
        }
    };

    return (
        <Link href={href} onClick={handleClick} className="block w-full md:max-w-[500px] mx-auto">
            <div className="bg-red border-black/10 cursor-pointer rounded-xl border p-s7 md:p-s9">
            <div className="flex gap-4">
                {/* Image container with fixed dimensions */}
                <div className="h-12 w-12 flex-shrink-0 md:h-[84px] md:w-[84px]">
                    <Image
                        src={data?.company?.logoUrl}
                        alt="Logo"
                        width={84}
                        height={84}
                        className="h-full w-full rounded-lg border-[0px] border-neutralOpacityDark-10 object-cover"
                    />
                </div>

                {/* Content wrapper with proper flex behavior */}
                <div className="flex min-w-0 flex-1 items-center justify-between">
                    {/* Text content with proper truncation */}
                    <div className="flex min-w-0 max-w-[calc(100%-60px)] flex-col md:gap-[8px] md:max-w-[calc(100%-80px)]">
                        <div className="overflow-hidden">
                            <h3 className="truncate text-left text-sm font-semibold leading-[23.4px] md:text-lg">
                                {data?.company?.displayName}
                            </h3>
                        </div>
                        <div className="whitespace-nowrap text-left">
                            {data?.staggeredPrice?.length > 0 ? (
                                <>
                                    <span className="text-sm font-medium md:text-md">
                                        {staggeredPriceMinValue
                                            ? formatIndianCurrency(staggeredPriceMinValue)
                                            : "--"}
                                    </span>
                                </>
                            ) : (
                                <>
                                    <span className="text-sm font-medium md:text-md">
                                        {formatIndianCurrency(data?.price?.price ?? 0)}
                                    </span>{" "}
                                    <span className="text-sm text-neutral-300">•</span>{" "}
                                    <span className="text-xs leading-[140%] text-neutral-300 md:text-sm">
                                        {`${data?.minUnits} ${data?.minUnits > 1 ? "shares" : "share"} min`}
                                    </span>
                                </>
                            )}
                            <span className="flex items-center rounded-[8px] py-1 text-xs">
                                {data?.productTagline && (
                                    <Badge
                                        label={data?.productTagline}
                                        showIcon={false}
                                        icon={<TopPicks />}
                                        variant={BadgeVariant.Success}
                                        scheme={BadgeScheme.Light}
                                        showCross={false}
                                    />
                                )}
                            </span>
                        </div>
                    </div>

                    {/* Button with fixed width */}
                    <div className="flex-shrink-0">
                        <IconButton
                            icon={<ArrowRight />}
                            className="h-[36px] w-[36px] min-h-[36px] min-w-[36px] border border-gray-300"
                            size={ButtonSize.Small}
                            variant={ButtonVariant.SecondaryDefault}
                        />
                    </div>
                </div>
            </div>
        </div>
        </Link>
    );
};

export default UEHeroCard;
